import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, Menu, MessageCircle, Sparkles, X } from "lucide-react";
import { useLang } from "@/i18n";

export const EASE = [0.16, 1, 0.3, 1];

export const IMG = {
  hero: "/art-hero.png",
  module: "/art-module.png",
  ev: "/art-ev.png",
  events: "/art-events.png",
  station: "/art-station.png",
  delivery: "/art-delivery.png",
  founders: "/founder-art.png",
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LogoMark = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <defs><linearGradient id="acg" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse"><stop stopColor="#7B2FF7" /><stop offset="1" stopColor="#F107A3" /></linearGradient></defs>
    <rect width="32" height="32" rx="8" fill="url(#acg)" />
    <path d="M17 4.5 8.5 17.5h6l-2 10 8.5-13h-6l2-10Z" fill="#fff" />
    <circle cx="23.8" cy="8.2" r="3.2" fill="#fff" />
  </svg>
);

export const Line = ({ children, delay = 0 }) => (
  <span className="mask-line">
    <motion.span className="mask-inner" initial={{ y: "115%" }} animate={{ y: "0%" }} transition={{ duration: 1.05, delay, ease: EASE }}>{children}</motion.span>
  </span>
);

export const Reveal = ({ children, className = "", delay = 0, y = 40, ...rest }) => (
  <motion.div className={className} initial={{ opacity: 0, y, filter: "blur(8px)" }} whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }} viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.95, delay, ease: EASE }} {...rest}>{children}</motion.div>
);

const marqueeItems = ["Energy-as-a-Service", "آسان چارج", "Clean energy", "Doorstep EV charging", "AsaanCharger1"];

export const Marquee = () => (
  <div className="marquee" aria-hidden="true">
    <div className="marquee-track">
      {[0, 1].map((dup) => (
        <div className="marquee-group" key={dup}>
          {marqueeItems.map((item, index) => (
            <span className="marquee-pair" key={item}>
              <span className={`marquee-item ${item === "آسان چارج" ? "urdu-item" : index % 2 ? "outline" : ""}`}>{item}</span>
              <Sparkles className="marquee-star" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const Nav = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { lang, toggle, t } = useLang();
  const close = () => setMenuOpen(false);
  const linkClass = ({ isActive }) => (isActive ? "active" : "");
  return (
    <header className="site-head">
      <nav className="nav container">
        <Link className="brand" to="/" data-testid="brand-link" onClick={close}><span className="brand-mark"><LogoMark /></span><span className="word">asaan<span>charge</span></span></Link>
        <div className={`nav-links ${menuOpen ? "open" : ""}`}>
          <NavLink to="/" end className={linkClass} onClick={close} data-testid="nav-home-link">{t("navHome")}</NavLink>
          <NavLink to="/platform" className={linkClass} onClick={close} data-testid="nav-platform-link">{t("navPlatform")}</NavLink>
          <NavLink to="/company" className={linkClass} onClick={close} data-testid="nav-company-link">{t("navCompany")}</NavLink>
          <Link className="nav-contact" to="/company#contact" onClick={close} data-testid="nav-contact-link">{t("navTalk")} <ArrowUpRight /></Link>
          <button className="lang-toggle" onClick={() => { toggle(); }} data-testid="lang-toggle" aria-label="Switch language">{lang === "en" ? "اردو" : "EN"}</button>
        </div>
        <button className="menu-button" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-button" aria-label="Toggle menu">{menuOpen ? <X /> : <Menu />}</button>
      </nav>
    </header>
  );
};

const OPTIONS = [["Business", "optBusiness"], ["EV Owner", "optEV"], ["Investor", "optInvestor"], ["Event Organiser", "optEvent"], ["Other", "optOther"]];

export const LeadForm = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", city: "", interest: "Business" });
  const [status, setStatus] = useState("");
  const submit = async (event) => { event.preventDefault(); setStatus(t("formSending")); try { await axios.post(`${API}/leads`, form); setStatus(t("formThanks")); setForm({ name: "", phone: "", city: "", interest: "Business" }); } catch (error) { setStatus(t("formFail")); } };
  return (
    <form className="lead-form" onSubmit={submit} data-testid="lead-form">
      <label>{t("formName")}<input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="lead-name-input" placeholder={t("phName")} /></label>
      <label>{t("formPhone")}<input required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone-input" placeholder="+92 300 1234567" /></label>
      <label>{t("formCity")}<input required value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="lead-city-input" placeholder={t("phCity")} /></label>
      <label>{t("formIam")}<select value={form.interest} onChange={e => setForm({ ...form, interest: e.target.value })} data-testid="lead-interest-select">{OPTIONS.map(([value, key]) => <option key={value} value={value}>{t(key)}</option>)}</select></label>
      <button className="button button-primary form-button" type="submit" data-testid="lead-submit-button">{t("formSubmit")} <ArrowUpRight /></button>
      {status && <p className="form-status" data-testid="lead-form-status">{status}</p>}
    </form>
  );
};

export const CtaBand = () => {
  const { t } = useLang();
  return (
    <section className="cta-band">
      <div className="container cta-wrap">
        <Reveal><h2>{t("ctaL1")}<br /><em>{t("ctaL2")}</em></h2></Reveal>
        <Reveal delay={0.12}><Link className="button button-cream" to="/company#contact" data-testid="cta-band-button">{t("ctaBtn")} <ArrowUpRight /></Link></Reveal>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer className="footer container">
      <Link className="brand" to="/" data-testid="footer-brand-link"><span className="brand-mark"><LogoMark /></span><span className="word">asaan<span>charge</span></span></Link>
      <p>{t("footerTag")}</p>
      <span className="urdu">آسان چارج</span>
      <small>{t("footerRights")}</small>
      <div className="footer-nav"><Link to="/" data-testid="footer-home-link">{t("navHome")}</Link><Link to="/platform" data-testid="footer-platform-link">{t("navPlatform")}</Link><Link to="/company" data-testid="footer-company-link">{t("navCompany")}</Link></div>
    </footer>
  );
};

export const WhatsFloat = () => {
  const { t } = useLang();
  return <a className="whatsapp-float" href="https://wa.me/923318882566" target="_blank" rel="noreferrer" data-testid="whatsapp-float-button"><MessageCircle /><span>{t("whats")}</span></a>;
};
